/**
 * @file ProductKindsPage.tsx
 * @description Справочник «Каталог → Виды товара» (phase19.22, inventory.md
 * W22, макет phase19.22-ui-design.md §4).
 *
 * Вид задаёт характеристики товаров: нужен ли срок годности и какой по
 * умолчанию, шаблон складской этикетки, регламент маркировки, ставку НДС,
 * допустим ли возврат в остаток. Страница = шапка в верхней панели + баннер
 * «У N товаров вид не задан» + список (`ResponsiveList`: таблица от 1024,
 * компактный список на телефоне; phase19.31, каркас справочника (б)) + форма в
 * шторке (открытая запись — в адресе `?edit=`) + окно «Проставить вид по
 * категориям».
 *
 * Раздел целиком под `SHOP_MANAGE_CATALOG` (маршрут каталога), отдельной
 * проверки прав кнопкам не нужно.
 */

import { Plus, Shapes } from "lucide-react";
import { useCallback, useState } from "react";

import { useEditParam } from "../../../components/catalog/directory/useEditParam";
import { ProductKindDeleteDialog } from "../../../components/catalog/product-kinds/ProductKindDeleteDialog";
import { ProductKindForm } from "../../../components/catalog/product-kinds/ProductKindForm";
import { PRODUCT_KIND_COLUMNS } from "../../../components/catalog/product-kinds/ProductKindList";
import { ProductKindMigrationDialog } from "../../../components/catalog/product-kinds/ProductKindMigrationDialog";
import { useProductKindActions } from "../../../components/catalog/product-kinds/useProductKindActions";
import { useProductKinds } from "../../../components/catalog/product-kinds/useProductKinds";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { NoticeStrip } from "../../../components/ui/NoticeStrip";
import { ResponsiveList } from "../../../components/ui/ResponsiveList";
import type { ProductKind } from "../../../lib/api-product-kinds";
import { pluralRu } from "../../../lib/plural";

export function ProductKindsPage() {
  const { kinds, productsWithoutKind, loading, error, refresh } = useProductKinds();
  const edit = useEditParam();
  const [migrating, setMigrating] = useState(false);
  const openEdit = useCallback((kind: ProductKind) => edit.open(kind.id), [edit]);
  const { actionsFor, deleting, setDeleting, confirmDelete, busy } = useProductKindActions({ onEdit: openEdit, refresh });

  // Архивные — в конце: с ними не работают, они справочные.
  const sorted = [...kinds].sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.sortOrder - b.sortOrder);
  const editing = edit.editId && !edit.isNew ? (kinds.find((k) => k.id === edit.editId) ?? null) : null;
  const formOpen = edit.isNew || editing !== null;
  const create = (
    <Button variant="outline" onClick={() => edit.open("new")}>
      <Plus className="size-4" />
      Вид товара
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Виды товара"
        subtitle="Срок годности, этикетка и НДС для товаров вида; вид задаётся у категории"
        action={
          <Button variant="brand" onClick={() => edit.open("new")}>
            <Plus className="size-4" />
            Вид товара
          </Button>
        }
      />

      {productsWithoutKind > 0 ? (
        // Кнопка — под текстом, а не в правом слоте: на 360 слот сжал бы текст.
        <NoticeStrip tone="warning">
          <p>
            У {productsWithoutKind} {pluralRu(productsWithoutKind, ["товара", "товаров", "товаров"])} вид не задан — у них
            не проверяется срок годности и не выбирается этикетка.
          </p>
          <Button variant="outline" className="mt-2 w-full sm:w-auto" onClick={() => setMigrating(true)}>
            Проставить по категориям
          </Button>
        </NoticeStrip>
      ) : null}

      <ResponsiveList
        aria-label="Виды товара"
        rows={sorted}
        columns={PRODUCT_KIND_COLUMNS}
        rowKey={(k) => k.id}
        rowHref={(k) => edit.hrefFor(k.id)}
        rowLabel={(k) => `Вид товара ${k.name}`}
        rowActions={actionsFor}
        rowActionsTitle={(k) => `Вид «${k.name}»`}
        loading={loading}
        error={error ? `Не удалось загрузить виды товара. ${error}` : null}
        onRetry={() => void refresh()}
        empty={{
          icon: Shapes,
          title: "Видов товара пока нет",
          description: "Заведите «Пищевой», «Косметика», «Непищевой» — от вида зависят срок годности и этикетка",
          action: create,
        }}
      />

      <ProductKindForm
        open={formOpen}
        kind={editing}
        onClose={edit.close}
        onSaved={() => {
          edit.close();
          void refresh();
        }}
      />

      <ProductKindMigrationDialog
        open={migrating}
        kinds={kinds}
        onClose={() => setMigrating(false)}
        onDone={() => {
          setMigrating(false);
          void refresh();
        }}
      />

      <ProductKindDeleteDialog kind={deleting} busy={busy} onCancel={() => setDeleting(null)} onConfirm={() => void confirmDelete()} />
    </div>
  );
}
